import CopyAllIcon from "@mui/icons-material/CopyAllRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineRounded";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import { Button, Stack, Tooltip } from "@mui/material";
import { useCallback, useMemo } from "react";
import { ReportAccessType } from "@entrilia/shared/api/reportAccessTypes.ts";
import { useClientPermissionsContext } from "@entrilia/shared/contexts/ClientPermissionsContext.ts";
import { checkAccess } from "@entrilia/shared/utilities/accessHelper.ts";
import type { ReportInfo } from "../../../../../../api/types/svTypes.ts";
import { CopyToEligibility } from "../components/CopyToEligibility";
import type { BulkActionReport } from "./toolbarActionsHelper.ts";
import { canCopyReport, canMoveReport, canShareReport, getBulkActionState } from "./toolbarActionsHelper.ts";

interface Props {
  selectedReports: (BulkActionReport & Partial<Pick<ReportInfo, "reportType">>)[];
  onCopy?: (ids: string[]) => void;
  onDelete?: (ids: string[]) => void;
  onMove?: (ids: string[]) => void;
  onUpdateSharing?: (ids: string[]) => void;
  hideCopy?: boolean;
}
export function CustomToolbarActions({ selectedReports, onCopy, onDelete, onMove, onUpdateSharing, hideCopy }: Props) {
  const { hasAnyPermissionForClient } = useClientPermissionsContext();

  const copyState = useMemo(() => {
    const copyableCount = selectedReports.filter(canCopyReport).length;
    return getBulkActionState(
      selectedReports,
      canCopyReport,
      `Copy Report${copyableCount > 1 ? "s" : ""}`,
      "Only the reports that aren't Financial Statements will be copied"
    );
  }, [selectedReports]);

  const deleteState = useMemo(
    () =>
      getBulkActionState(
        selectedReports,
        (report) => checkAccess(report.authorization.access, ReportAccessType.Owner),
        "Delete",
        "Only the reports to which you have appropriate access will be deleted"
      ),
    [selectedReports]
  );

  const moveState = useMemo(
    () =>
      getBulkActionState(
        selectedReports,
        (report) => canMoveReport(report, hasAnyPermissionForClient),
        "Change Group",
        "Only the reports to which you have appropriate access will be updated"
      ),
    [selectedReports, hasAnyPermissionForClient]
  );

  const shareState = useMemo(
    () =>
      getBulkActionState(
        selectedReports,
        (report) => canShareReport(report, hasAnyPermissionForClient),
        "Change Reports Sharing",
        "Only the reports to which you have appropriate access will be updated"
      ),
    [selectedReports, hasAnyPermissionForClient]
  );

  const handleOnCopy = useCallback(() => {
    onCopy?.(copyState.availableReports.map((r) => r.reportId));
  }, [onCopy, copyState.availableReports]);

  const handleOnDelete = useCallback(() => {
    onDelete?.(deleteState.availableReports.map((r) => r.reportId));
  }, [onDelete, deleteState.availableReports]);

  const handleOnMove = useCallback(() => {
    onMove?.(moveState.availableReports.map((r) => r.reportId));
  }, [onMove, moveState.availableReports]);

  const handleOnUpdateSharing = useCallback(() => {
    onUpdateSharing?.(shareState.availableReports.map((r) => r.reportId));
  }, [onUpdateSharing, shareState.availableReports]);

  return (
    <Stack direction="row" sx={{ gap: 1 }}>
      {!hideCopy && copyState.availableReports.length > 0 && (
        <CopyToEligibility>
          <Tooltip title={copyState.tooltipText} arrow disableInteractive>
            <Button color="secondary" variant="text" startIcon={<CopyAllIcon />} onClick={handleOnCopy}>
              {copyState.buttonText}
            </Button>
          </Tooltip>
        </CopyToEligibility>
      )}
      {onMove && moveState.availableReports.length > 0 && (
        <Tooltip title={moveState.tooltipText} arrow disableInteractive>
          <Button color="secondary" variant="text" startIcon={<DriveFileMoveOutlinedIcon />} onClick={handleOnMove}>
            {moveState.buttonText}
          </Button>
        </Tooltip>
      )}
      {onUpdateSharing && shareState.availableReports.length > 1 && (
        <Tooltip title={shareState.tooltipText} arrow disableInteractive>
          <Button color="secondary" variant="text" startIcon={<ShareOutlinedIcon />} onClick={handleOnUpdateSharing}>
            {shareState.buttonText}
          </Button>
        </Tooltip>
      )}
      {onDelete && deleteState.availableReports.length > 0 && (
        <Tooltip title={deleteState.tooltipText} arrow disableInteractive>
          <Button variant="text" color="error" startIcon={<DeleteOutlineIcon color="error" />} onClick={handleOnDelete}>
            {deleteState.buttonText}
          </Button>
        </Tooltip>
      )}
    </Stack>
  );
}
